import { useCallback, useState } from 'react';
import {
  CheckCircle,
  HourglassEmpty,
  Security,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { LoadingState } from './loading-state';
import { formatDateTime } from '../utils/formatting';
import {
  useGetV2AdminOrganizationsByOrganizationIdDataSecurityQuery,
  usePutV2AdminOrganizationsByOrganizationIdBaaMutation,
  usePutV2AdminOrganizationsByOrganizationIdCoiMutation,
  usePutV2AdminOrganizationsByOrganizationIdDataRetentionMutation,
} from '../store/hooks';
import { useSnackbar } from 'notistack';

interface OrganizationDataSecuritySectionProps {
  organizationId: string;
}

type ServiceType = 'baa' | 'coi' | 'retention';
type StatusValue = 'not_configured' | 'processing' | 'active';

interface UpdateDialogState {
  open: boolean;
  service: ServiceType | null;
  currentStatus: string;
  newStatus: StatusValue;
  notes: string;
}

const STATUS_OPTIONS: { value: StatusValue; label: string }[] = [
  { value: 'not_configured', label: 'Not Configured' },
  { value: 'processing', label: 'Processing' },
  { value: 'active', label: 'Active' },
];

const getStatusChip = (status: string) => {
  const normalizedStatus = status.toLowerCase();
  
  if (normalizedStatus === 'active') {
    return (
      <Chip
        icon={<CheckCircle sx={{ fontSize: 18 }} />}
        label="Active"
        size="small"
        sx={{
          fontWeight: 600,
          px: 1,
          backgroundColor: theme => alpha(theme.palette.success.main, 0.1),
          color: theme => theme.palette.success.dark,
          border: '1px solid',
          borderColor: theme => theme.palette.success.dark,
          '& .MuiChip-icon': {
            color: theme => theme.palette.success.dark,
          },
        }}
      />
    );
  }
  
  if (normalizedStatus === 'processing') {
    return (
      <Chip
        icon={<HourglassEmpty sx={{ fontSize: 18 }} />}
        label="Processing"
        size="small"
        sx={{
          fontWeight: 600,
          px: 1,
          backgroundColor: theme => alpha(theme.palette.warning.main, 0.1),
          color: theme => theme.palette.warning.dark,
          border: '1px solid',
          borderColor: theme => theme.palette.warning.dark,
          '& .MuiChip-icon': {
            color: theme => theme.palette.warning.dark,
          },
        }}
      />
    );
  }
  
  return (
    <Chip
      label="Not Configured"
      size="small"
      sx={{
        fontWeight: 600,
        px: 1,
        backgroundColor: theme => alpha(theme.palette.grey[500], 0.1),
        color: theme => theme.palette.grey[700],
        border: '1px solid',
        borderColor: theme => theme.palette.grey[400],
      }}
    />
  );
};

export function OrganizationDataSecuritySection({
  organizationId,
}: OrganizationDataSecuritySectionProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [updateDialog, setUpdateDialog] = useState<UpdateDialogState>({
    open: false,
    service: null,
    currentStatus: '',
    newStatus: 'not_configured',
    notes: '',
  });

  const {
    data: dataSecurityData,
    isLoading,
    refetch,
  } = useGetV2AdminOrganizationsByOrganizationIdDataSecurityQuery(
    { organizationId },
    { skip: !organizationId }
  );

  const [updateBAA, { isLoading: isUpdatingBAA }] =
    usePutV2AdminOrganizationsByOrganizationIdBaaMutation();
  const [updateCOI, { isLoading: isUpdatingCOI }] =
    usePutV2AdminOrganizationsByOrganizationIdCoiMutation();
  const [updateRetention, { isLoading: isUpdatingRetention }] =
    usePutV2AdminOrganizationsByOrganizationIdDataRetentionMutation();

  const isUpdating = isUpdatingBAA || isUpdatingCOI || isUpdatingRetention;

  const handleUpdateClick = useCallback(
    (service: ServiceType, currentStatus: string) => {
      // Map current status to valid StatusValue for the dialog
      // API can return 'not_entitled', 'not_configured', 'processing', 'active'
      // Dialog only allows: 'not_configured', 'processing', 'active'
      let mappedStatus: StatusValue = 'not_configured';
      if (currentStatus === 'processing') {
        mappedStatus = 'processing';
      } else if (currentStatus === 'active') {
        mappedStatus = 'active';
      }
      // 'not_entitled' and 'not_configured' both map to 'not_configured'
      
      setUpdateDialog({
        open: true,
        service,
        currentStatus,
        newStatus: mappedStatus,
        notes: '',
      });
    },
    []
  );

  const handleUpdateClose = useCallback(() => {
    setUpdateDialog({
      open: false,
      service: null,
      currentStatus: '',
      newStatus: 'not_configured',
      notes: '',
    });
  }, []);

  const handleUpdateConfirm = useCallback(async () => {
    if (!updateDialog.service) return;

    try {
      const request = {
        organizationId,
        adminUpdateStatusRequest: {
          status: updateDialog.newStatus,
          notes: updateDialog.notes.trim() || null,
        },
      };

      if (updateDialog.service === 'baa') {
        await updateBAA(request).unwrap();
      } else if (updateDialog.service === 'coi') {
        await updateCOI(request).unwrap();
      } else if (updateDialog.service === 'retention') {
        await updateRetention(request).unwrap();
      }

      enqueueSnackbar('Status updated successfully', { variant: 'success' });
      handleUpdateClose();
      void refetch();
    } catch (error: any) {
      enqueueSnackbar(
        error?.data?.message || error?.message || 'Failed to update status',
        { variant: 'error' }
      );
    }
  }, [updateDialog, organizationId, updateBAA, updateCOI, updateRetention, enqueueSnackbar, handleUpdateClose, refetch]);

  if (isLoading) {
    return <LoadingState message="Loading data security status..." />;
  }

  if (!dataSecurityData) {
    return (
      <Alert severity="info">No data security information available for this organization.</Alert>
    );
  }

  const { baa, coi, data_retention } = dataSecurityData;

  return (
    <Box>
      <Card>
        <CardHeader
          avatar={<Security />}
          title="Data Security Services"
          subheader="View and manage BAA, COI, and Data Retention requests"
        />
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* BAA Section */}
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Business Associate Agreement (BAA)</Typography>
                  {getStatusChip(baa.status)}
                </Box>

                {baa.status !== 'not_entitled' && baa.status !== 'not_configured' && (
                  <Box sx={{ mb: 2 }}>
                    {baa.entity_name && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Entity Name:</strong> {baa.entity_name}
                      </Typography>
                    )}
                    {baa.requested_at && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Requested:</strong> {formatDateTime(baa.requested_at)}
                      </Typography>
                    )}
                    {baa.activated_at && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Activated:</strong> {formatDateTime(baa.activated_at)}
                      </Typography>
                    )}
                    {baa.request_id && (
                      <Typography variant="body2" sx={{ mb: 1, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        <strong>Request ID:</strong> {baa.request_id}
                      </Typography>
                    )}
                  </Box>
                )}

                {baa.status === 'not_configured' && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No request submitted yet
                  </Typography>
                )}

                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleUpdateClick('baa', baa.status)}
                  disabled={isUpdating}
                >
                  Update Status
                </Button>
              </CardContent>
            </Card>

            {/* COI Section */}
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Certificate of Insurance (COI)</Typography>
                  {getStatusChip(coi.status)}
                </Box>

                {coi.status !== 'not_entitled' && coi.status !== 'not_configured' && (
                  <Box sx={{ mb: 2 }}>
                    {coi.business_name && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Business Name:</strong> {coi.business_name}
                      </Typography>
                    )}
                    {coi.requested_at && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Requested:</strong> {formatDateTime(coi.requested_at)}
                      </Typography>
                    )}
                    {coi.activated_at && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Activated:</strong> {formatDateTime(coi.activated_at)}
                      </Typography>
                    )}
                    {coi.request_id && (
                      <Typography variant="body2" sx={{ mb: 1, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        <strong>Request ID:</strong> {coi.request_id}
                      </Typography>
                    )}
                  </Box>
                )}

                {coi.status === 'not_configured' && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No request submitted yet
                  </Typography>
                )}

                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleUpdateClick('coi', coi.status)}
                  disabled={isUpdating}
                >
                  Update Status
                </Button>
              </CardContent>
            </Card>

            {/* Data Retention Section */}
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Data Retention Policy</Typography>
                  {getStatusChip(data_retention.status)}
                </Box>

                {data_retention.status !== 'not_entitled' && data_retention.status !== 'not_configured' && (
                  <Box sx={{ mb: 2 }}>
                    {data_retention.unsigned_notes_retention_days !== null && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Retention Days:</strong> {data_retention.unsigned_notes_retention_days} days
                      </Typography>
                    )}
                    {data_retention.configured_at && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Configured:</strong> {formatDateTime(data_retention.configured_at)}
                      </Typography>
                    )}
                    {data_retention.activated_at && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Activated:</strong> {formatDateTime(data_retention.activated_at)}
                      </Typography>
                    )}
                    {data_retention.request_id && (
                      <Typography variant="body2" sx={{ mb: 1, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        <strong>Request ID:</strong> {data_retention.request_id}
                      </Typography>
                    )}
                  </Box>
                )}

                {data_retention.status === 'not_configured' && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No policy configured yet
                  </Typography>
                )}

                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleUpdateClick('retention', data_retention.status)}
                  disabled={isUpdating}
                >
                  Update Status
                </Button>
              </CardContent>
            </Card>
          </Box>
        </CardContent>
      </Card>

      {/* Update Status Dialog */}
      <Dialog open={updateDialog.open} onClose={handleUpdateClose} fullWidth maxWidth={"sm"}>
        <DialogTitle>
          Update {updateDialog.service === 'baa' ? 'BAA' : updateDialog.service === 'coi' ? 'COI' : 'Data Retention'} Status
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Current Status: <strong>{updateDialog.currentStatus}</strong>
            </Typography>

            <FormControl fullWidth>
              <InputLabel>New Status</InputLabel>
              <Select
                value={updateDialog.newStatus}
                label="New Status"
                onChange={e =>
                  setUpdateDialog(prev => ({
                    ...prev,
                    newStatus: e.target.value as StatusValue,
                  }))
                }
                disabled={isUpdating}
              >
                {STATUS_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Admin Notes (Optional)"
              multiline
              rows={3}
              value={updateDialog.notes}
              onChange={e =>
                setUpdateDialog(prev => ({ ...prev, notes: e.target.value }))
              }
              placeholder="Add notes for audit trail..."
              disabled={isUpdating}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUpdateClose} disabled={isUpdating}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateConfirm}
            variant="contained"
            disabled={isUpdating || updateDialog.newStatus === updateDialog.currentStatus}
          >
            {isUpdating ? 'Updating...' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
